import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { BUSINESS, QUICK_FEATURES } from "@/data/site";
import { scrollToId } from "@/lib/scroll";
import { useLang } from "@/i18n";
import Icon from "@/components/Icon";
import { EASE } from "@/components/Reveal";

const LINE_COLORS = ["text-solar", "text-cyan2", "text-electric"];

export default function Hero() {
    const { t } = useLang();
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const yOrbA = useTransform(scrollYProgress, [0, 1], [0, 180]);
    const yOrbB = useTransform(scrollYProgress, [0, 1], [0, -120]);
    const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

    return (
        <section
            id="home"
            ref={ref}
            data-testid="hero-section"
            className="relative min-h-screen flex items-center overflow-hidden circuit-bg pt-28 pb-16"
        >
            <div className="absolute inset-0 hero-glow pointer-events-none" />
            <motion.div
                style={{ y: yOrbA }}
                className="absolute -top-24 -right-24 w-[34rem] h-[34rem] rounded-full bg-electric/20 blur-[130px] pointer-events-none"
            />
            <motion.div
                style={{ y: yOrbB }}
                className="absolute bottom-0 -left-32 w-[28rem] h-[28rem] rounded-full bg-cyan2/10 blur-[120px] pointer-events-none"
            />
            <div className="absolute top-1/3 left-[12%] w-2 h-2 rounded-full bg-cyan2 animate-pulse-dot pointer-events-none" />
            <div className="absolute top-[22%] right-[18%] w-1.5 h-1.5 rounded-full bg-solar animate-pulse-dot pointer-events-none" style={{ animationDelay: "0.9s" }} />
            <div className="absolute bottom-[28%] right-[38%] w-1.5 h-1.5 rounded-full bg-electric animate-pulse-dot pointer-events-none" style={{ animationDelay: "1.6s" }} />

            <motion.div style={{ opacity: fade }} className="relative max-w-7xl mx-auto px-6 md:px-12 w-full">
                <motion.p
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, ease: EASE }}
                    className="font-mono2 text-xs md:text-sm tracking-[0.3em] uppercase text-cyan2 mb-8"
                    data-testid="hero-eyebrow"
                >
                    {t.hero.eyebrow}
                </motion.p>

                <h1 className="font-heading font-bold tracking-tighter leading-[1.04] text-4xl sm:text-6xl lg:text-7xl xl:text-[5.2rem] text-ink" data-testid="hero-headline">
                    {t.hero.lines.map((line, i) => (
                        <span key={i} className="block overflow-hidden pb-1">
                            <motion.span
                                className="block"
                                initial={{ y: "112%" }}
                                animate={{ y: 0 }}
                                transition={{ duration: 0.95, delay: 0.18 + i * 0.17, ease: EASE }}
                            >
                                {line.text}
                                <span className={LINE_COLORS[i]}>{line.accent}</span>
                            </motion.span>
                        </span>
                    ))}
                </h1>

                <motion.p
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.85, ease: EASE }}
                    className="mt-8 max-w-xl text-base md:text-lg text-ice/60 leading-relaxed"
                    data-testid="hero-subtext"
                >
                    {t.hero.sub}
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 1.0, ease: EASE }}
                    className="mt-10 flex flex-wrap items-center gap-4"
                >
                    <motion.a
                        data-testid="hero-call-now"
                        href={BUSINESS.tel}
                        whileHover={{ scale: 1.03 }}
                        whileTap={{ scale: 0.96 }}
                        className="inline-flex items-center gap-3 rounded-full bg-gradient-to-r from-electric to-cyan2 px-8 py-4 font-bold text-ink shadow-[0_0_40px_rgba(18,110,255,0.35)] hover:shadow-[0_0_55px_rgba(0,207,255,0.55)] transition-shadow duration-300"
                    >
                        <Icon name="phone" size={18} />
                        {t.hero.call} — {BUSINESS.phoneDisplay}
                    </motion.a>
                    <motion.button
                        data-testid="hero-explore-products"
                        onClick={() => scrollToId("products")}
                        whileHover={{ scale: 1.03 }}
                        whileTap={{ scale: 0.96 }}
                        className="inline-flex items-center gap-2 rounded-full border-2 border-ink/20 px-8 py-[0.9rem] font-semibold text-ink hover:border-cyan2 hover:text-cyan2 transition-colors duration-300"
                    >
                        {t.hero.explore}
                        <Icon name="arrow" size={17} />
                    </motion.button>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 1.2, ease: EASE }}
                    className="mt-16 flex flex-wrap gap-3"
                    data-testid="hero-quick-features"
                >
                    {QUICK_FEATURES.map((f, i) => (
                        <div
                            key={f.icon}
                            data-testid={`quick-feature-${f.icon}`}
                            className="flex items-center gap-2.5 glass rounded-full px-5 py-2.5 text-sm text-ink/85 hover:border-cyan2/40 hover:text-cyan2 transition-colors duration-300 animate-float-slow"
                            style={{ animationDelay: `${i * 0.7}s` }}
                        >
                            <Icon name={f.icon} size={16} className="text-cyan2" />
                            {t.hero.chips[i]}
                        </div>
                    ))}
                </motion.div>
            </motion.div>
        </section>
    );
}
